// ============================================================================
// useAiReportScopeControls
// Shared UI/state wrapper for report-scoped AI evidence filters + AI Data.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AiReportScopePanel, { AiScopeButton } from '@process-advisor/process-miner/consultant/AiReportScopePanel';
import AiDataPanel, { AiDataButton } from '@process-advisor/process-miner/consultant/AiDataPanel';
import { applySampleItemPrivacy } from '@process-advisor/process-miner/consultant/attributePrivacyUtils';
import {
  applyAiReportScopeToEvidence,
  loadAiReportScopeBackend,
  loadAiReportScopeLocal,
  normalizeAiReportScope,
  DEFAULT_AI_SAMPLE_ITEM_LIMIT,
  MAX_AI_SAMPLE_ITEM_LIMIT,
} from '@process-advisor/process-miner/consultant/aiReportScope';

const noopArray = () => [];
const noopRules = () => ({});

export const countAiReportScopeRules = (scope = {}) => {
  if (!scope?.enabled) return 0;
  return (
    (scope.excludeVariantIds?.length || 0) +
    (scope.excludeWorkItems?.length || 0) +
    (scope.excludePaths?.length || 0) +
    (scope.excludeFieldValues?.length || 0) +
    (scope.instructions?.trim() ? 1 : 0) +
    ((Number(scope.sampleItemLimit) || DEFAULT_AI_SAMPLE_ITEM_LIMIT) !== DEFAULT_AI_SAMPLE_ITEM_LIMIT ? 1 : 0)
  );
};

const resolveButtonState = (scope, source, isDirty = false) => {
  if (isDirty) return 'unsaved';
  if (!scope?.enabled) return 'off';
  if (source === 'report') return 'report';
  if (source === 'global') return 'default';
  return 'report';
};

export function useAiReportScopeControls({
  modelId,
  reportKey,
  reportLabel,
  getTaskRows = noopArray,
  getVariants = noopArray,
  getProcessSteps = noopArray,
  getPrivacyRules = noopRules,
}) {
  const [showAiScopePanel, setShowAiScopePanel] = useState(false);
  const [showAiDataPanel, setShowAiDataPanel] = useState(false);
  const [aiReportScope, setAiReportScope] = useState(() => {
    const local = loadAiReportScopeLocal(modelId, reportKey);
    return normalizeAiReportScope(local || {});
  });
  const [scopeSource, setScopeSource] = useState(() => loadAiReportScopeLocal(modelId, reportKey) ? 'local' : 'none');
  const [scopeDirty, setScopeDirty] = useState(false);

  const getTaskRowsForScope = useCallback(() => {
    try {
      return getTaskRows(MAX_AI_SAMPLE_ITEM_LIMIT) || [];
    } catch (_) {
      return getTaskRows() || [];
    }
  }, [getTaskRows]);

  useEffect(() => {
    let cancelled = false;
    loadAiReportScopeBackend(modelId, reportKey).then((remote) => {
      if (cancelled) return;
      if (remote?.scope) {
        setAiReportScope(normalizeAiReportScope(remote.scope || {}));
        setScopeSource(remote.source || 'model');
        setScopeDirty(false);
      } else {
        const local = loadAiReportScopeLocal(modelId, reportKey);
        setAiReportScope(normalizeAiReportScope(local || {}));
        setScopeSource(local ? 'local' : 'none');
        setScopeDirty(false);
      }
    });
    return () => { cancelled = true; };
  }, [modelId, reportKey]);

  const handleScopeDraftChange = useCallback((scope, sourceHint) => {
    setAiReportScope(normalizeAiReportScope(scope || {}));
    setScopeSource(sourceHint || scopeSource || 'report');
    setScopeDirty(true);
  }, [scopeSource]);

  const handleScopeSaved = useCallback((scope, savedSource) => {
    setAiReportScope(normalizeAiReportScope(scope || {}));
    setScopeSource(savedSource || 'report');
    setScopeDirty(false);
  }, []);

  const getScopedEvidence = useCallback(() => applyAiReportScopeToEvidence({
    rawSampleItems: getTaskRowsForScope() || [],
    processSteps: getProcessSteps() || [],
    variants: getVariants() || [],
    scope: aiReportScope,
  }), [getTaskRowsForScope, getProcessSteps, getVariants, aiReportScope]);

  const getScopedTaskRows = useCallback(() => getScopedEvidence().rawSampleItems || [], [getScopedEvidence]);
  const getScopedProcessSteps = useCallback(() => getScopedEvidence().processSteps || [], [getScopedEvidence]);
  const getScopedVariants = useCallback(() => getScopedEvidence().variants || [], [getScopedEvidence]);
  const getReportScopeSummary = useCallback(() => getScopedEvidence().summary || {}, [getScopedEvidence]);

  const privacyPreview = useMemo(() => {
    try {
      return applySampleItemPrivacy(getScopedTaskRows(), getPrivacyRules());
    } catch (_) {
      return { excludedFields: [], anonymizedFields: [] };
    }
  }, [getScopedTaskRows, getPrivacyRules, aiReportScope]);

  const controls = (
    <>
      <AiScopeButton
        onClick={() => setShowAiScopePanel(v => !v)}
        state={resolveButtonState(aiReportScope, scopeSource, scopeDirty)}
        count={countAiReportScopeRules(aiReportScope)}
      />
      <AiDataButton
        onClick={() => setShowAiDataPanel(v => !v)}
        excludedCount={privacyPreview.excludedFields?.length || 0}
        anonymizedCount={privacyPreview.anonymizedFields?.length || 0}
        isActive={showAiDataPanel}
      />
    </>
  );

  const panels = (
    <>
      <AiReportScopePanel
        isOpen={showAiScopePanel}
        onClose={() => setShowAiScopePanel(false)}
        modelId={modelId}
        reportKey={reportKey}
        reportLabel={reportLabel}
        scope={aiReportScope}
        scopeSource={scopeSource}
        onScopeChange={handleScopeDraftChange}
        onScopeSaved={handleScopeSaved}
        getTaskRows={getTaskRowsForScope}
        getVariants={getVariants}
        getProcessSteps={getProcessSteps}
      />
      <AiDataPanel
        isOpen={showAiDataPanel}
        onClose={() => setShowAiDataPanel(false)}
        getTaskRows={getScopedTaskRows}
        getPrivacyRules={getPrivacyRules}
        applySampleItemPrivacyFn={applySampleItemPrivacy}
      />
    </>
  );

  return {
    aiReportScope,
    setAiReportScope,
    controls,
    panels,
    getScopedEvidence,
    getScopedTaskRows,
    getScopedProcessSteps,
    getScopedVariants,
    getReportScopeSummary,
  };
}

export default useAiReportScopeControls;
